import { FC, useState, useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { makeSxStyles } from "ui";

import AddressBoxComponent from "../../pages/dashboard/address-box-component/address-box-component";
import { MapMarker } from "./map.types";
import SidebarNotesView from "../../pages/dashboard/customer-notes/sidebar-notes-view";
import { RouteStatus } from "../../pages/dashboard/route-planner/helpers/route-status.enum";

export interface SelectedAddresesSidebarProps {
  customHeader?: React.ReactNode;
  invalidSelection?: boolean;
  removeSelected: (id: string) => void;
  selectedAddresses: MapMarker[];
  view: "route-planner" | "region-management";
  excludeAddressValidation?: boolean;
  serviceTimeMap?: Record<string, number>;
  adjustedServiceTimeMap?: Record<string, number>;
  adjustedEstimatedTimeMinutesMap?: Record<string, number>;
  currentRouteStatus?: RouteStatus; // For precise formatting in live phase
}

export type SelectedAddressForNotes = {
  id: string;
  address?: string;
};

const SelectedAddresesSidebar: FC<SelectedAddresesSidebarProps> = (props) => {
  const {
    customHeader,
    invalidSelection,
    selectedAddresses,
    removeSelected,
    excludeAddressValidation = false,
    view,
    serviceTimeMap,
    adjustedServiceTimeMap,
    adjustedEstimatedTimeMinutesMap,
    currentRouteStatus,
  } = props;
  const styles = useStyles();
  const [selectedAddressForNotes, setSelectedAddressForNotes] = useState<
    SelectedAddressForNotes | undefined
  >(undefined);

  // Sync selectedAddressForNotes with changes in selectedAddresses
  useEffect(() => {
    if (selectedAddressForNotes) {
      // Find the current address in the selectedAddresses array
      const currentAddress = selectedAddresses.find(
        (addr) => addr.id === selectedAddressForNotes.id,
      );

      if (!currentAddress) {
        // Address was removed from selection
        if (selectedAddresses.length === 1) {
          // If only one address is selected, set it as the selectedAddressForNotes
          setSelectedAddressForNotes({
            id: selectedAddresses[0].id,
            address: selectedAddresses[0].address,
          });
        } else {
          // Otherwise, clear the notes view
          setSelectedAddressForNotes(undefined);
        }
      }
      // If currentAddress exists, do nothing (keep the current selectedAddressForNotes)
    }
  }, [selectedAddresses, selectedAddressForNotes]);

  const handleViewNotesClick = (selectedAddress: SelectedAddressForNotes) => {
    setSelectedAddressForNotes(selectedAddress);
  };

  const handleCloseNotes = () => {
    setSelectedAddressForNotes(undefined);
  };

  return (
    <Grid sx={styles.regionSidebar} item marginTop={8}>
      {selectedAddressForNotes ? (
        <SidebarNotesView
          selectedAddressForNotes={selectedAddressForNotes}
          onCloseNotes={handleCloseNotes}
        />
      ) : (
        <>
          {customHeader}
          {excludeAddressValidation && (
            <Box sx={styles.invalidSelection}>
              <Typography
                color="#690005"
                fontWeight={400}
                data-testid="invalid-selection-body"
              >
                Addresses being plowed are excluded from bulk actions.
              </Typography>
            </Box>
          )}
          {invalidSelection && (
            <Box sx={styles.invalidSelection}>
              <Typography
                color="#690005"
                fontWeight={600}
                mb="5px"
                data-testid="invalid-selection-header"
              >
                Incorrect selection of addresses!
              </Typography>
              <Typography
                color="#690005"
                fontWeight={400}
                data-testid="invalid-selection-body"
              >
                {view === "route-planner"
                  ? "Selected addresses that do not belong to the same shift and route and do not follow a sequence, need to be re-sequenced before moving to a new or same route/shift"
                  : "Selected addresses that do not belong to the same region and do not follow a sequence, need to be re-sequenced before moving to a new or same region"}
              </Typography>
            </Box>
          )}

          {selectedAddresses &&
            selectedAddresses.map((address) => {
              return (
                <AddressBoxComponent
                  {...address}
                  selected={true}
                  key={address.id}
                  onHandleChange={(id) => removeSelected(id)}
                  onViewNotes={(selectedAddress: SelectedAddressForNotes) =>
                    handleViewNotesClick(selectedAddress)
                  }
                  view={view}
                  serviceTimeMap={serviceTimeMap}
                  adjustedServiceTimeMap={adjustedServiceTimeMap}
                  adjustedEstimatedTimeMinutesMap={
                    adjustedEstimatedTimeMinutesMap
                  }
                  currentRouteStatus={currentRouteStatus}
                />
              );
            })}
        </>
      )}
    </Grid>
  );
};

const useStyles = makeSxStyles(({ palette }) => ({
  regionSidebar: {
    width: "317px",
    padding: "12px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    height: "calc(100vh - 138px)",
    overflow: "auto",
  },
  invalidSelection: {
    width: "100%",
    padding: "16px",
    backgroundColor: palette.error.main,
    borderRadius: "4px",
  },
}));

export default SelectedAddresesSidebar;
